import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ControlledTabs } from '@/components/ui';
import { LivePageConnectionIndicator } from '@/components/ui/LivePageConnectionIndicator';
import { routes } from '@/lib/routes';

const TABS = [
  { id: 'integrations', label: 'Integrations' },
  { id: 'google-workspace', label: 'Google Workspace' },
  { id: 'mcp-servers', label: 'MCP servers' },
  { id: 'plugin-catalog', label: 'Plugin catalog' },
  { id: 'providers', label: 'Identity Providers' },
  { id: 'kbs', label: 'Knowledge bases' },
];

/**
 * Shared shell for org-admin list pages. Renders the section heading and a
 * tab bar that switches between the org-level resource lists, then yields to
 * the matched child route via `<Outlet />`. Detail and form routes (`/new`,
 * `/i/:id`, `/mcp/:id`) deliberately sit outside this layout — once a user is
 * inside a specific entity, the tabs would be misleading.
 */
export default function OrgAdminLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeTab = pathname.startsWith(routes.console.org.knowledgeBases)
    ? 'kbs'
    : pathname.startsWith(routes.console.org.identityProviders)
      ? 'providers'
      : pathname.startsWith(routes.console.org.pluginCatalog)
        ? 'plugin-catalog'
        : pathname.startsWith(routes.console.org.googleWorkspace)
          ? 'google-workspace'
          : pathname.startsWith(routes.console.org.mcpServers)
            ? 'mcp-servers'
            : 'integrations';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Organization</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Organization-level resources available to every space. Admin-only.
          </p>
        </div>
        <LivePageConnectionIndicator className="mt-2 mr-1 shrink-0" />
      </div>
      <ControlledTabs
        tabs={TABS}
        activeTab={activeTab}
        onChange={(id) => {
          if (id === 'kbs') navigate(routes.console.org.knowledgeBases);
          else if (id === 'providers') navigate(routes.console.org.identityProviders);
          else if (id === 'plugin-catalog') navigate(routes.console.org.pluginCatalog);
          else if (id === 'google-workspace') navigate(routes.console.org.googleWorkspace);
          else if (id === 'mcp-servers') navigate(routes.console.org.mcpServers);
          else navigate(routes.console.org.integrations);
        }}
      >
        <Outlet />
      </ControlledTabs>
    </div>
  );
}
